import { cva, type VariantProps } from "class-variance-authority";
import type { ComponentPropsWithRef } from "react";

import { cn } from "../lib/cn";

/**
 * `shimmer` plutôt qu'un clignotement d'opacité : un balayage lumineux qui
 * traverse le bloc se lit comme « quelque chose arrive », là où une opacité qui
 * pulse se lit comme « quelque chose ne va pas ». La direction du mouvement
 * suggère une progression, même quand il n'y en a aucune à afficher.
 */
const skeletonVariants = cva("shimmer bg-surface-sunken", {
  variants: {
    shape: {
      /** Ligne de texte. Combinée à `w-*` pour simuler des longueurs inégales. */
      text: "h-4 rounded-sm",
      block: "rounded-xl",
      circle: "rounded-full",
    },
  },
  defaultVariants: { shape: "text" },
});

export interface SkeletonProps
  extends ComponentPropsWithRef<"div">,
    VariantProps<typeof skeletonVariants> {}

/**
 * Fantôme de contenu en cours de chargement.
 *
 * `aria-hidden` est délibéré : le squelette n'a aucun sens à l'oral. C'est au
 * conteneur qui charge de porter `aria-busy="true"`, ce qui annonce l'attente
 * une seule fois au lieu d'énumérer dix rectangles vides.
 */
export function Skeleton({ className, shape, ...props }: SkeletonProps) {
  return (
    <div aria-hidden="true" className={cn(skeletonVariants({ shape }), className)} {...props} />
  );
}

/** Largeurs décroissantes puis dernière ligne courte — le rythme d'un vrai paragraphe. */
const LINE_WIDTHS = ["w-full", "w-11/12", "w-full", "w-10/12", "w-9/12"] as const;

export interface SkeletonTextProps extends ComponentPropsWithRef<"div"> {
  lines?: number;
}

/**
 * Paragraphe fantôme.
 *
 * Des lignes toutes de même largeur se lisent comme un tableau, pas comme du
 * texte : l'irrégularité est ce qui rend le squelette crédible. La dernière est
 * systématiquement plus courte, comme une fin de paragraphe réelle.
 */
export function SkeletonText({ className, lines = 3, ...props }: SkeletonTextProps) {
  return (
    <div aria-hidden="true" className={cn("flex w-full flex-col gap-2", className)} {...props}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={index === lines - 1 ? "w-7/12" : (LINE_WIDTHS[index % LINE_WIDTHS.length] ?? "w-full")}
        />
      ))}
    </div>
  );
}

export { skeletonVariants };
